/**
 * ===========================
 * ActionButtonConfirmStatusWithPolling - Implementation Template
 * ===========================
 *
 * This component wires an ActionButton -> ModalDialogConfirmStatusWithPolling
 * and expects polling behavior via props.
 *
 * To implement a new "start process -> poll -> terminal action" button:
 *
 * 1) startRequest: () => Promise<TStartResponse>
 *    - Called once after user confirms.
 *    - Should START the backend process.
 *    - Must return the initial process model (often includes id).
 *    - Throw to show an immediate error in the dialog.
 *
 * 2) pollRequest: (processId: string) => Promise<TProcess>
 *    - Called repeatedly (pollIntervalMs) until the process is terminal.
 *    - Must return the latest process model by id.
 *    - Keep it lightweight and safe to call often.
 *
 * 3) onTerminal?: (process: TProcess) => Promise<void>
 *    - Called once AFTER polling ends (terminal state reached).
 *    - Use for side effects: download files, navigate, show toast, etc.
 *    - Throw to surface an error in the dialog.
 *
 * Example usage:
 *
 * const startRequest = async () => api.startMyProcess(...).then(r => r.data);
 * const pollRequest = (id: string) => api.getProcess(id).then(r => r.data);
 * const onTerminal = async (process: MyProcess) => {
 *   if (process.processState !== ProcessState.COMPLETE) return;
 *   const response = await api.getFile(...); // responseType: "blob"
 *   saveBlobToFile(response.data, "MyFile.zip");
 * };
 *
    <ActionButtonConfirmStatusWithPolling<
      ExportToExcelProcess,
      ExportToExcelProcess
    >
      //Button Props
      variant="outlined"
      color="primary"
      disabled={!productLineId}
      //buttonFullWidth={true}
      //Icon Props
      icon={TableChartOutlinedIcon}
      iconOnly={false}
      //Tooltip Props
      tooltipText="Export full productline to Excel"
      tooltipArrow={true}
      tooltipPlacement="right"
      //Dialog Props
      maxWidth="sm"
      fullWidth
      dialogTitle="Export Productline to Excel"
      disableEscapeKeyDown={true}
      disableCloseOnBackdropClick={true}
      //Content Section Props
      contentSection={contentSection}
      hideContentOnProcessComplete={true}
      //Action Button Props
      defaultButton="OK"
      cancelButtonText="Cancel"
      okButtonText="Export"
      okButtonWarningColor={false}
      //Process Polling Options
      processPollingOptions={processPollingOptions}
      buildResultSection={buildResultSection}
      resetOnOpen={true}
    >
      Export to Excel
    </ActionButtonConfirmStatusWithPolling>
 */

import React, { useCallback, useState } from "react";
import { ActionButton, ActionButtonProps } from "components";
import { ProcessBase } from "types/process";
import {
  ModalDialogConfirmStatusWithPolling,
  ModalDialogConfirmStatusWithPollingProps,
} from "components";

type DialogPassthroughProps<
  TStartResponse,
  TProcess extends ProcessBase
> = Omit<
  ModalDialogConfirmStatusWithPollingProps<TStartResponse, TProcess>,
  "open" | "title" | "onClose" | "onCancelButtonClick"
>;

export interface ActionButtonConfirmStatusWithPollingProps<
  TStartResponse,
  TProcess extends ProcessBase
> extends DialogPassthroughProps<TStartResponse, TProcess> {
  // Button props
  ariaLabel?: ActionButtonProps["ariaLabel"];
  children?: ActionButtonProps["children"]; //Button text / content (ignored when iconOnly is true)
  color?: ActionButtonProps["color"];
  disabled?: ActionButtonProps["disabled"];
  buttonFullWidth?: ActionButtonProps["fullWidth"];
  size?: ActionButtonProps["size"];
  variant?: ActionButtonProps["variant"];

  // Icon props
  icon?: ActionButtonProps["icon"];
  iconOnly?: ActionButtonProps["iconOnly"];

  //Tooltip Props
  tooltipText?: ActionButtonProps["tooltipText"];
  tooltipArrow?: ActionButtonProps["tooltipArrow"];
  tooltipPlacement?: ActionButtonProps["tooltipPlacement"];

  // Dialog props
  dialogTitle?: string;

  /**
   * Called immediately before the dialog opens.
   * Intended for resetting parent-owned state (if any).
   */
  onDialogOpen?: () => void;
}

export function ActionButtonConfirmStatusWithPolling<
  TStartResponse,
  TProcess extends ProcessBase
>(props: ActionButtonConfirmStatusWithPollingProps<TStartResponse, TProcess>) {
  const {
    // Button Props
    ariaLabel,
    children,
    color = "primary",
    disabled = false,
    buttonFullWidth = false,
    size = "medium",
    variant = "contained",

    // Icon props
    icon,
    iconOnly = false,

    // Tooltip props
    tooltipText = "",
    tooltipArrow = true,
    tooltipPlacement = "top",

    // Dialog props
    dialogTitle = "Confirm Action",

    // Lifecycle hook
    onDialogOpen,

    // Everything else passes through to the dialog wrapper
    ...otherProps
  } = props;

  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => {
    onDialogOpen?.();
    setOpen(true);
  }, [onDialogOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      <ActionButton
        ariaLabel={ariaLabel}
        color={color}
        disabled={disabled}
        fullWidth={buttonFullWidth}
        size={size}
        variant={variant}
        onClick={handleOpen}
        icon={icon}
        iconOnly={iconOnly}
        tooltipText={tooltipText}
        tooltipArrow={tooltipArrow}
        tooltipPlacement={tooltipPlacement}
      >
        {children}
      </ActionButton>

      <ModalDialogConfirmStatusWithPolling<TStartResponse, TProcess>
        {...otherProps}
        // Dialog props
        open={open}
        title={dialogTitle}
        onClose={handleClose}
        //Action Button Props
        onCancelButtonClick={handleClose}
      />
    </>
  );
}
